import * as React from "react";
import {Router} from "react-router-dom";
import {createBrowserHistory} from "history";

import {Helmet}             from "@Common/libs/helmet/helmet";
import {loadGA4}            from "@Common/loaders/load-ga4";
import {cs}                 from "@Common/react/chain-services";
import {consumeContext,
        provideContext,
        provideContext1}    from "@Common/react/context";
import {Invoke}             from "@Common/react/invoke";
import {Load}               from "@Common/react/load";
import {UseState}           from "@Common/react/use-state";
import {CcApis3}            from "@Common/tools/cc-integration/cc_apis/cc-apis-3";
import {CcApis3Mock}        from "@Common/tools/cc-integration/cc_apis/mock/cc-apis-3-mock";
import {isDevQuan}          from "@Common/ui-components/debug/is-dev-quan";
import {parseUrlQuery}      from "@Common/utils/parse-url-query";
import {removeURLParameter} from "@Common/utils/remove-url-parameter";

import {Tooltip}                    from "./common/tool-tip";
import {loadApis}                   from "./loaders/apis/apis";
import {loadAccountMenu}            from "./loaders/load-account-menu";
import {loadBraintree}              from "./loaders/load-braintree";
import {loadCart2}                  from "./loaders/load-cart-2";
import {loadCatSubStyle}            from "./loaders/load-catSubStyle";
import {loadCompany}                from "./loaders/load-company";
import {loadDevTools}               from "./loaders/load-dev-tools";
import {LoadFacebookAutoLogin}      from "./loaders/load-facebook-auto-login";
import {LoadGoogleAutoLogin}        from "./loaders/load-google-auto-login";
import {loadMenu}                   from "./loaders/load-menus";
import {loadScheme}                 from "./loaders/load-scheme";
import {loadSvgThumbnailsCache}     from "./loaders/load-svg-thumbnails-cache";
import {loadSystemConstants}        from "./loaders/load-system-constants";
import {loadTemplateOptionsCache}   from "./loaders/load-template-options-cache";
import {loadUserLevels}             from "./loaders/load-user-levels";
import {loadUserCreatedCatSubStyle} from "./loaders/load-userCreatedCatSubStyle";
import {loadSession}                from "./loaders/session/load-session";
import {SystemMessages}             from "./loaders/system-messages/system-messages";
import {ErrorBoundary}              from "./routes/common/error-boundary";
import {RouteSwitch}                from "./routes/route-switch";

export const App: React.FC = () => cs(
    ({}, next) => loadDevTools({next}),
    (_, next) => (
        <ErrorBoundary {...{
            next,
        }}/>
    ),

    ({}, next) => loadApis({
        next,
        onUnauthorized: () => {
            window.location.href = "/app";
        },
    }),

    ({}, next) => cs(
        ["query", ({}, next) => next(parseUrlQuery(window.location.search))],
        ({query}, next) => ((query && query['token']) ? (
            ValidateXpressoToken({xpressoToken: query['token'], next})
        ) : (
            next()
        )),
        ["companyXdocsUrl", ({}, next) => next(window.location.pathname.split("/")[2] ?? '')],
        ["themeScheme", ({companyXdocsUrl}, next) => loadScheme({next, companyXdocsUrl})],
        // For now, other APIs has to wait until theme scheme finish, or PHP server will mess up the "account manager" data
        ({themeScheme}, next) => !themeScheme ? (
            "Loading theme..."
        ) : (
            next()
        ),
        ({themeScheme}, next) => (
            <>
                <Helmet title={
                    themeScheme?.companyName === 'Xpressdocs' ? (
                        "Xpressdocs"
                    ) : (
                        themeScheme?.companyName + " | Xpressdocs"
                    )
                }>
                    <link rel="icon" href={themeScheme?.xpresso.favicon} />
                    <meta name="description" content={
                        `${
                            themeScheme?.companyName === 'Xpressdocs' ? 'Brand' : themeScheme?.companyName
                        } business cards, postcards, brochures and more. Order all your ${
                            themeScheme?.companyName === 'Xpressdocs' ? 'Brand' : themeScheme?.companyName
                        } material with same-day shipping on most products.`}/>
                    <meta name="keywords" content={`${
                        themeScheme?.companyName?.split(' ').join(',')
                    },Xpressdocs`.replace(/[^a-zA-Z0-9]+/g, ',')}/>
                </Helmet>
                {next()}
            </>
        ),
        ({themeScheme}, next) =>  provideContext({routerHistory: createBrowserHistory({
            basename: process.env.PUBLIC_URL + (themeScheme?.companyXdocsUrl ? ('/' + themeScheme?.companyXdocsUrl) : ''),
        })}, next),
        consumeContext("apis"),
        ({apis, themeScheme}, next) => themeScheme.type === "guest" &&  themeScheme?.enableGuestCheckout ? (
            <>
                {Invoke({
                    action: async () => {
                        const result = await apis.next.authenticate.startGuestSession(themeScheme.companyId)
                        if( !result )
                        {
                            document.cookie = "disable_google_auto_login=0; path=/"
                            document.cookie = "disable_facebook_auto_login=0; path=/"
                            await themeScheme.reload()
                        }
                        else
                            window.location.href = "/";
                    }
                })}
                Loading theme...
            </>
        ) : next(),
        ({themeScheme}, next) => themeScheme.type === "loggedIn" ? cs(
            ["userLevels", (_, next) => loadUserLevels(next)],
            ["company", ({}, next) => loadCompany(next)],
            ["session", ({company, userLevels}, next) => loadSession({company, userLevels, next})],
            ({}, next) => loadMenu(next),
            ["systemConstants", ({}, next) => loadSystemConstants({next})],
            ({systemConstants}, next) => cs(
                ["ccApis", (_, next) => next((isDevQuan() ? CcApis3Mock : CcApis3)({host: systemConstants.value?.DS_SERVER_URL}))],
                ({ccApis}) => provideContext1("ccApis", ccApis, next),
            ),
            ({}, next) => loadAccountMenu(next),
            ({}, next) => loadCatSubStyle(next),
            ({}, next) => loadUserCreatedCatSubStyle(next),
            ({}, next) => loadNotifications(next),
            ({}, next) => loadCart2(next),
            ({}, next) => loadTemplateOptionsCache(next),
            ({}, next) => loadBraintree({next}),
            ({}, next) => loadSvgThumbnailsCache(next),

            ({userLevels, session}, next) => LoadingMessages({session, userLevels, themeScheme, next}),
            ({userLevels}, next) => provideContext({
                userLevels: {...userLevels.value, reload: userLevels.reload, onChange: userLevels.onChange},
                themeScheme,
            }, next),
            ({}, next) => SystemMessages({next}),
            next,
        ) : cs(
            ({}, next) => provideContext({themeScheme}, next),
            ({}, next) => loadSystemConstants({next}),
            ({}, next) => loadNotifications(next),
            next,
        ),
        next,
    ),
    ({}, next) => loadGA4({app:'xpresso', next}),
    ({}, next) => loadCountryCodes({next}),
    ({}, next) => createGlobalState({stateName: "neighborhoodPolygons", initValue: {}, next}),
    consumeContext("routerHistory"),
    ({routerHistory}) => (
        <div style={{height: "100%"}}>
            <Tooltip/>
            <LoadFacebookAutoLogin />
            <LoadGoogleAutoLogin />
            <Router history={routerHistory}>
                <RouteSwitch/>
            </Router>
        </div>
    )
);

const loadCountryCodes = ({next}) => cs(
    consumeContext("apis"),
    ["countryCodes", ({apis}, next) => Load({
        fetch: () => apis.next.getCountryCodes(),
        next,
    })],
    ({countryCodes}) => provideContext({countryCodes}, next),
);

const loadNotifications = (next) => cs(
    consumeContext("apis"),
    ["notifyMsgs", ({apis}, next) => Load({
        fetch: () => apis.next.getNotifications(),
        next,
    })],
    ({notifyMsgs}) => provideContext({notifyMsgs}, next),
);

const LoadingMessages = ({session, userLevels, themeScheme, next}) => (
    session.loading ? (
        "Loading user session..."
    ) : userLevels.loading ? (
        "Loading user level..."
    ) : !themeScheme ? (
        "Loading theme..."
    ) : (
        next()
    )
);

const ValidateXpressoToken = ({xpressoToken, next}) => cs(
    consumeContext("apis"),
    ["token", ({apis}, next) => apis && Load({
        fetch: () => apis.next.validateXpressoToken({xpressoToken}),
        next,
    })],
    ({token}, next) => token && next(),
    ({}) => {
        const newUrl = removeURLParameter({
            url: window.location.toString(),
            parameter: 'token'
        });
        if(newUrl) {
            window.history.replaceState(window.history.state, null, newUrl);
        }
        return next();
    },
);

const createGlobalState = ({stateName, initValue, next}) => cs(
    ["state", (_, next) => UseState({initValue, next})],
    ({state}) => provideContext1(stateName, state, next),
);
